import React, { ChangeEvent, useEffect, useState } from "react";

import { cloneDeep, isEqual, uniqWith } from "lodash";
import { useHistory, useParams } from "react-router-dom";

import { useAuth, useTealium } from "@hubcapp/context";

import { AMOUNT_TO_FETCH, DEFAULT_OFFSET } from "constants/messages";
import { Message, MessagesProps } from "./types";
import { formatMessageObjects } from "./helpers";
import { pageHeaderPropsMap } from "./helpers";
import { startCase } from "lodash";
import MessagesLayout from "./MessagesLayout";
import useMutationCreateMessage from "./hooks/useMutationCreateMessage";
import useQueryHistoricMessages from "./hooks/useQueryHistoricMessages";
import useQueryPollingMessages from "./hooks/useQueryPollingMessages";

const MessagesLayoutStateProvider: React.FC<MessagesProps> = () => {
  const {
    instructor: { id: instructorId, firstName, lastName },
  } = useAuth();
  const { selectedTab = "messages" } = useParams<MessagesProps>();
  const history = useHistory();
  const { trackEvent } = useTealium();

  const [message, setMessage] = useState<string>("");
  const [offset, setOffset] = useState(DEFAULT_OFFSET);
  const [messages, setMessages] = useState<Message[]>([]);
  const [moreAvailable, setMoreAvailable] = useState(true);
  const historic = useQueryHistoricMessages(instructorId, {
    offset,
    total: AMOUNT_TO_FETCH,
  });

  const {
    data: pollingData,
    startPolling,
    stopPolling,
  } = useQueryPollingMessages(instructorId, {
    offset: DEFAULT_OFFSET,
    total: AMOUNT_TO_FETCH,
  });

  useEffect(() => {
    startPolling(1000);
    return () => {
      stopPolling();
    };
  }, [startPolling, stopPolling]);

  useEffect(() => {
    if (pollingData) {
      const incomingMessages = cloneDeep(pollingData.instructor.messages);
      const currentMessages = cloneDeep(messages);
      const newMessagesArray = [...incomingMessages, ...currentMessages];
      const removedDuplicatesArray = uniqWith(newMessagesArray, (a, b) =>
        isEqual(a, b),
      );
      setMessages(removedDuplicatesArray);
    }
  }, [pollingData]);

  useEffect(() => {
    if (!historic.data) return;
    if (historic.data.instructor.messages.length === 0) {
      setMoreAvailable(false);
      return;
    }
    const incomingMessages = cloneDeep(historic.data.instructor.messages);
    const currentMessages = cloneDeep(messages);
    const newMessagesArray = [...currentMessages, ...incomingMessages];
    setMessages(newMessagesArray);
  }, [historic.data]);

  const loadMore = () => {
    setOffset(offset + AMOUNT_TO_FETCH);
  };

  const onCompleted = () => {
    setMessage("");
  };

  const [createMessage] = useMutationCreateMessage({
    onCompleted: onCompleted,
  });

  const handleChange = (newValue: string) => {
    trackEvent({
      eventCategory: "Messages",
      eventAction: "Interaction",
      eventLabel: startCase(newValue),
    });
    if (newValue === "messages") return history.push("/messages");
    return history.push(`/messages/${newValue}`);
  };

  const sendMessage = () => {
    createMessage({
      variables: {
        instructorId: instructorId,
        body: message,
      },
    });

    trackEvent({
      eventCategory: "Messages",
      eventAction: "Interaction",
      eventLabel: "Send A Message",
    });
  };

  return (
    <MessagesLayout
      selectedTab={selectedTab}
      onTabChange={handleChange}
      PageHeaderProps={pageHeaderPropsMap[selectedTab]}
      message={message}
      setMessage={(event: ChangeEvent<HTMLInputElement>) =>
        setMessage(event.target.value)
      }
      instructorName={`${firstName} ${lastName}`}
      sendMessage={sendMessage}
      loadMore={loadMore}
      moreMessagesAvailable={moreAvailable}
      messages={formatMessageObjects(messages, `${firstName} ${lastName}`)}
    />
  );
};

export default MessagesLayoutStateProvider;
